// Port of SuiteModel/Sentences.swift: sentence boundaries, found one way everywhere — Statistics (Readability, Sentence
// lengths), Shape, the style linter, continuity, story-time suggestions, the glossary, and History's TextDiff.
//
// Approximate: the Mac uses `NLTokenizer(unit: .sentence)`; this uses `Intl.Segmenter` (Unicode's sentence rules,
// UAX #29), then joins a break after the titles and initials NLTokenizer doesn't split after ("Mr. Utterson",
// "Dr. Jekyll", "said I. Then"), which UAX #29 splits before any capital. Which ones those are was measured on the Mac
// (macOS 27.0, each word in several sentences): the answers were the same in every sentence, and they are NLTokenizer's
// own, not a grammar's — it splits after "Messrs." and "St.", and after the initials C, H, J, N, P, R, V, W and Y, but
// never after "I." or the other capitals. Each range keeps its trailing whitespace, as NLTokenizer's tokens do (the
// Swift's callers trim or search inside ranges), so the ranges tile the text.

interface Segment { segment: string; index: number }
interface Segmenter { segment(text: string): Iterable<Segment> }

let shared: Segmenter | null = null;
function segmenter(): Segmenter {
  if (!shared) shared = new (Intl as unknown as { Segmenter: new (l?: string, o?: object) => Segmenter }).Segmenter('en', { granularity: 'sentence' });
  return shared;
}

/** Words ending in a full stop that NLTokenizer keeps inside a sentence (the others it was tried with all split:
 *  Mx, Messrs, Mmes, Drs, Revd, Hon, St, Ft, Fr, Sr, Jr, Snr, Jnr, Cpt, Gen, Cmdr, Adm, Brig, Insp, Supt, Det, Gov, Sen,
 *  Pres, Mme, Mlle, Esq, cf, viz). */
const ABBREVIATIONS = new Set(['mr', 'mrs', 'ms', 'dr', 'prof', 'rev', 'mt', 'capt', 'col', 'lt', 'sgt', 'maj', 'rep', 'vs', 'e.g', 'i.e']);

/** The single capitals NLTokenizer does split after ("He wrote to J. Utterson" is two sentences on the Mac). */
const SPLITTING_INITIALS = new Set(['C', 'H', 'J', 'N', 'P', 'R', 'V', 'W', 'Y']);

/** Whether a segment ends in an abbreviation that is followed by more of the same sentence. */
function endsInAbbreviation(segment: string): boolean {
  const m = /(?:^|[\s("“‘'\[—–-])([\p{L}.]+)\.\s+$/u.exec(segment);
  if (!m) return false;
  const word = m[1];
  if (/^\p{Lu}$/u.test(word)) return !SPLITTING_INITIALS.has(word); // an initial, "I" among them
  return ABBREVIATIONS.has(word.toLowerCase());
}

/** `Sentences.nsRanges(in:)`: the sentences of `text` as UTF-16 [start, end) ranges, in order. */
export function sentenceRanges(text: string): [number, number][] {
  if (!text) return [];
  const out: [number, number][] = [];
  let pendingStart: number | null = null;
  for (const { segment, index } of segmenter().segment(text)) {
    const start: number = pendingStart ?? index;
    const end = index + segment.length;
    // Only join within a line: a paragraph never runs on.
    if (endsInAbbreviation(segment) && !/\n/.test(segment) && end < text.length) {
      pendingStart = start;
      continue;
    }
    out.push([start, end]);
    pendingStart = null;
  }
  if (pendingStart !== null) out.push([pendingStart, text.length]);
  return out;
}

/** The sentences of `text` as strings (each keeps its trailing whitespace, so they rejoin exactly). */
export function sentenceTexts(text: string): string[] {
  return sentenceRanges(text).map(([a, b]) => text.slice(a, b));
}
